import { useState } from "react";
import dayjs from "dayjs";
import { PrimaryButton } from "@normasteaching/norma-ui";
import {
  BonificoOrderDTO,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { useUpdateBonificoOrderNotes } from "../../ProductService/BonificoOrder/useUpdateBonificoOrderNotes.ts";

const NOTES_MAX_LENGTH = 2000;

export function BonificoOrderNotes({
  order,
  onSaved,
}: {
  order: BonificoOrderDTO;
  onSaved?: (order: BonificoOrderDTO) => void;
}) {
  const [savedOrder, setSavedOrder] = useState(order);
  const [draftNotes, setDraftNotes] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { updateNotes, loading } = useUpdateBonificoOrderNotes();
  const notes = savedOrder.notes ?? "";

  const save = async () => {
    setError(null);
    try {
      const updated = await updateNotes(order.orderId, draftNotes ?? "");
      setSavedOrder(updated);
      setDraftNotes(null);
      onSaved?.(updated);
    } catch (e) {
      setError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante il salvataggio della nota. Riprova.",
        ),
      );
    }
  };

  if (draftNotes !== null)
    return (
      <div className="flex flex-col gap-2">
        <textarea
          aria-label="Nota dell'ordine"
          value={draftNotes}
          maxLength={NOTES_MAX_LENGTH}
          rows={4}
          autoFocus
          onChange={(e) => setDraftNotes(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none"
        />
        <div className="flex items-center gap-3">
          <span className="flex-1 text-xs text-gray-400">
            {draftNotes.length}/{NOTES_MAX_LENGTH}
          </span>
          {error && <span className="text-xs text-red-500">{error}</span>}
          <button
            type="button"
            onClick={() => {
              setDraftNotes(null);
              setError(null);
            }}
            className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
          >
            Annulla
          </button>
          <PrimaryButton
            label={loading ? "Salvataggio..." : "Salva nota"}
            onClick={save}
            disabled={loading}
          />
        </div>
      </div>
    );

  return (
    <div className="flex flex-col gap-2">
      {notes ? (
        <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
          {notes}
        </p>
      ) : (
        <p className="text-sm text-gray-400">Nessuna nota</p>
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setDraftNotes(notes)}
          className="text-sm text-blue-600 hover:underline"
        >
          {notes ? "Modifica nota" : "Aggiungi nota"}
        </button>
        {savedOrder.notesUpdatedAtUtc && (
          <span className="text-xs text-gray-400">
            Aggiornata il{" "}
            {dayjs(savedOrder.notesUpdatedAtUtc).format("DD/MM/YYYY HH:mm")}
          </span>
        )}
      </div>
    </div>
  );
}
